import { useState, useMemo, useCallback } from 'react';

interface UseTimeInputControllerProps {
    composedEvent: any;
    updateComposedEvent: (updates: any) => void;
}

/**
 * useTimeInputController: Headless logic for managing Start/End/Duration selection.
 * V1.0: Encapsulates modal state and temporal commit logic.
 */
export const useTimeInputController = ({ composedEvent, updateComposedEvent }: UseTimeInputControllerProps) => {
    const [activeTimePickerModal, setActiveTimePickerModal] = useState<'START' | 'END' | 'DURATION' | null>(null);

    // 1. DURATION CALCULATION
    const currentDuration = useMemo(() => {
        if (!composedEvent) return 0;
        const start = new Date(`${composedEvent.startDate}T${composedEvent.startTime}`);
        const end = new Date(`${composedEvent.endDate}T${composedEvent.endTime}`);
        return (end.getTime() - start.getTime()) / 60000;
    }, [composedEvent?.startDate, composedEvent?.startTime, composedEvent?.endDate, composedEvent?.endTime]);

    // 2. COMMIT LOGIC
    const commitTimeChange = useCallback((updates: { date?: string, time?: string, duration?: number }) => {
        if (!activeTimePickerModal || !composedEvent) return;

        if (activeTimePickerModal === 'START') {
            const newDate = updates.date || composedEvent.startDate;
            const newTime = updates.time || composedEvent.startTime;
            updateComposedEvent({ startDate: newDate, startTime: newTime });
        } else if (activeTimePickerModal === 'END') {
            const newDate = updates.date || composedEvent.endDate;
            const newTime = updates.time || composedEvent.endTime;
            updateComposedEvent({ endDate: newDate, endTime: newTime });
        } else if (activeTimePickerModal === 'DURATION' && updates.duration !== undefined) {
            // Recalculate end time based on fixed start + new duration
            const start = new Date(`${composedEvent.startDate}T${composedEvent.startTime}`);
            const newEnd = new Date(start.getTime() + updates.duration * 60000);
            
            const endYear = newEnd.getFullYear();
            const endMonth = (newEnd.getMonth() + 1).toString().padStart(2, '0');
            const endDay = newEnd.getDate().toString().padStart(2, '0');
            const endHour = newEnd.getHours().toString().padStart(2, '0');
            const endMin = newEnd.getMinutes().toString().padStart(2, '0');

            updateComposedEvent({
                endDate: `${endYear}-${endMonth}-${endDay}`,
                endTime: `${endHour}:${endMin}`
            });
        }
    }, [activeTimePickerModal, composedEvent, updateComposedEvent]);

    return {
        activeTimePickerModal,
        setActiveTimePickerModal,
        currentDuration,
        commitTimeChange
    };
};
